<template>
  <DiagnosisCard heading="お客様とのマッチ度">
    <template #content>
      <li
        v-for="(item, index) in matchScoreItems"
        :key="index"
        :class="$style.listItem"
      >
        <DolphinChip
          :class="[$style.chip, chipClass(item.name)]"
          :text="item.name"
        />

        <DolphinChip :class="[$style.matchScore]">
          マッチ度
          <li v-for="i in MAX_MATCH_SCORE" :key="i" :class="$style.hearts">
            <Heart :class="heartClass(i, item.score)" />
          </li>
        </DolphinChip>
      </li>
    </template>
  </DiagnosisCard>
</template>

<script setup lang="ts">
import DolphinChip from '@/components/atoms/DolphinChip.vue'
import DiagnosisCard from '@/components/molecules/DiagnosisCard.vue'
import { useCssModule } from 'vue'
import Heart from '@/assets/images/icons/fill/heart.svg?component'
import type { AiMatchScoreMatchScoreFragment } from './MatchScore.generated'

const MAX_MATCH_SCORE = 3

const props = defineProps<{
  aiMatchScore: AiMatchScoreMatchScoreFragment
}>()

const toNumOfHeartsFromAiMatchScore =
  (lower: number, higher: number) => (score: number) =>
    score < lower ? 1 : score < higher ? 2 : 3

const fromOnetimeSaleMatchingScore = toNumOfHeartsFromAiMatchScore(0, 3)
const fromLifePlanningMatchingScore = toNumOfHeartsFromAiMatchScore(1, 5)
const fromSellingHomeMatchingScore = toNumOfHeartsFromAiMatchScore(3, 6)
const fromRealEstateInvestmentMatchingScore = toNumOfHeartsFromAiMatchScore(
  2,
  8
)
const fromJobChangeMatchingScore = toNumOfHeartsFromAiMatchScore(0, 5)
const fromHouseholdImprovementMatchingScore = toNumOfHeartsFromAiMatchScore(
  3,
  8
)

const matchScoreItems = [
  {
    name: '資産運用',
    score: fromOnetimeSaleMatchingScore(
      props.aiMatchScore.onetimeSaleMatchingScore
    ),
  },
  {
    name: 'ライフプラン',
    score: fromLifePlanningMatchingScore(
      props.aiMatchScore.lifePlanningMatchingScore
    ),
  },
  {
    name: '実需住宅',
    score: fromSellingHomeMatchingScore(
      props.aiMatchScore.sellingHomeMatchingScore
    ),
  },
  {
    name: '不動産投資',
    score: fromRealEstateInvestmentMatchingScore(
      props.aiMatchScore.realEstateInvestmentMatchingScore
    ),
  },
  {
    name: '転職',
    score: fromJobChangeMatchingScore(
      props.aiMatchScore.jobChangeMatchingScore
    ),
  },
  {
    name: '家計改善',
    score: fromHouseholdImprovementMatchingScore(
      props.aiMatchScore.householdImprovementMatchingScore
    ),
  },
]

const style = useCssModule()
const chipClass = (name: string) => {
  switch (name) {
    case '資産運用':
      return style['chip--assetManagement']
    case 'ライフプラン':
      return style['chip--lifePlanning']
    case '実需住宅':
      return style['chip--realEstate']
    case '不動産投資':
      return style['chip--realEstateInvestment']
    case '転職':
      return style['chip--jobChange']
    case '家計改善':
      return style['chip--householdImprovement']
  }
}

const heartClass = (index: number, matchScore: number) => [
  style.hearts__item,
  {
    [style['hearts__item--pink']]: index <= matchScore,
  },
]
</script>

<style lang="scss" module>
.listItem {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
}

.chip {
  font-size: 12px;
  font-weight: bold;
  color: $color-text-and-object-high-emphasis-inverse;

  &--assetManagement {
    background-color: $color-label-blue;
  }

  &--lifePlanning {
    background-color: $color-label-green;
  }

  &--realEstate {
    background-color: $color-label-purple;
  }

  &--realEstateInvestment {
    background-color: $color-label-orange;
  }

  &--jobChange {
    background-color: $color-label-teal;
  }

  &--householdImprovement {
    background-color: $color-label-red;
  }
}

.matchScore {
  font-size: 12px;
  font-weight: bold;
  background-color: $color-yellow-200;
}

.hearts {
  display: flex;
  align-items: center;
  margin-left: 8px;

  &__item {
    fill: $color-text-and-object-low-emphasis;

    &--pink {
      fill: $color-red-500;
    }
  }

  &:nth-child(n + 2) {
    margin-left: 4px;
  }
}
</style>
